import { useState, useEffect } from "react";
import { FileUpload } from "@/components/FileUpload";
import { MultiFileUpload } from "@/components/MultiFileUpload";
import { ProgressTimeline } from "@/components/ProgressTimeline";
import { HairQuestionnaire, HairQuestionnaireData } from "@/components/HairQuestionnaire";
import { ReportCounterScreen } from "@/components/analysis/ReportCounterScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/contexts/I18nContext";
import { useTenant } from "@/contexts/TenantContext";
import { supabase } from "@/integrations/supabase/client";
import { Sparkles, CheckCircle2, Mail, Phone, User, X, ShieldCheck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import logoSavoy from "@/assets/logo-savoy-optimized.png";
import Footer from "@/components/Footer";
import TrustSection from "@/components/TrustSection";
import FlagLanguageSwitcher from "@/components/FlagLanguageSwitcher";
import { ConsentBanner } from "@/components/ConsentBanner";
import { trackingClient } from "@/lib/tracking/client";
import DOMPurify from "dompurify";

type AnalysisMode = "skin" | "hair";

// Cache interface
interface UserProfileCache {
  full_name: string;
  phone: string;
  email: string;
  cached_at: number;
}

const CACHE_KEY = "savoy_user_profile";
const CACHE_EXPIRY_DAYS = 30;

export default function Index() {
  const { t, locale, setLocale } = useI18n();
  const { currentTenant, refreshTenant, isLoading: tenantLoading } = useTenant();
  const navigate = useNavigate();

  // Redirect to new analyze flow
  useEffect(() => {
    if (currentTenant && !tenantLoading) {
      // Determine which product type to redirect to
      let productType = 'skin'; // default
      
      if (currentTenant.skinFlowEnabled && !currentTenant.hairFlowEnabled) {
        productType = 'skin';
      } else if (!currentTenant.skinFlowEnabled && currentTenant.hairFlowEnabled) {
        productType = 'hair';
      } else if (currentTenant.skinFlowEnabled && currentTenant.hairFlowEnabled) {
        // Both enabled, default to skin
        productType = 'skin';
      }
      
      navigate(`/analyze?product=${productType}`, { replace: true });
    }
  }, [currentTenant, tenantLoading, navigate]);
  
  const STEPS_SKIN = [
    { label: t('timeline.steps.validate'), duration: 10 },
    { label: t('timeline.steps.analyze_skin'), duration: 45 },
    { label: t('timeline.steps.generate'), duration: 25 },
    { label: t('timeline.steps.email'), duration: 10 },
  ];

  const STEPS_HAIR = [
    { label: t('timeline.steps.validate'), duration: 10 },
    { label: t('timeline.steps.analyze_hair'), duration: 45 },
    { label: t('timeline.steps.generate'), duration: 25 },
    { label: t('timeline.steps.email'), duration: 10 },
  ];
  
  const [mode, setMode] = useState<AnalysisMode | null>(null);
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
  });
  const [hairQuestionnaire, setHairQuestionnaire] = useState<HairQuestionnaireData>({
    age: "",
    gender: "",
    hasMedicalHistory: false,
    medicalHistoryDetails: "",
    hasFamilyHistory: false,
    familyHistoryDetails: "",
    diet: "",
    sleepHours: "",
    stressLevel: "",
    hairCareRoutine: [],
    hairCareNotes: "",
  });
  const [questionnaireComplete, setQuestionnaireComplete] = useState(false);
  const [questionnaireErrors, setQuestionnaireErrors] = useState<any>({});
  const [emailConsent, setEmailConsent] = useState(false);
  const [skinPhotos, setSkinPhotos] = useState<{ front: string; left: string; right: string }>({ front: "", left: "", right: "" });
  const [hairPhoto, setHairPhoto] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isCachedUser, setIsCachedUser] = useState(false);
  const [trackingInitialized, setTrackingInitialized] = useState(false);
  const [formStartTracked, setFormStartTracked] = useState(false);
  const { toast } = useToast();

  const STEPS = !mode ? STEPS_SKIN : (mode === "skin" ? STEPS_SKIN : STEPS_HAIR);

  // Initialize tracking and set default mode when tenant loads
  useEffect(() => {
    if (currentTenant && !trackingInitialized) {
      trackingClient.init(currentTenant.id, 'default');
      setTrackingInitialized(true);
      
      // Auto-select mode if only one flow is enabled
      if (currentTenant.skinFlowEnabled && !currentTenant.hairFlowEnabled) {
        setMode("skin");
      } else if (!currentTenant.skinFlowEnabled && currentTenant.hairFlowEnabled) {
        setMode("hair");
      } else if (currentTenant.skinFlowEnabled && currentTenant.hairFlowEnabled) {
        // Both enabled, default to skin
        setMode("skin");
      }
    }
  }, [currentTenant, trackingInitialized]);

  // Auto-switch to enabled language if current locale is not enabled
  useEffect(() => {
    if (currentTenant?.languagesEnabled) {
      const languagesEnabled = currentTenant.languagesEnabled;
      const localeMapping: Record<string, string> = {
        'en': 'en',
        'ar-AE': 'ar-AE',
        'he': 'he'
      };
      
      const currentLocaleKey = localeMapping[locale];
      const isCurrentLocaleEnabled = languagesEnabled[currentLocaleKey] !== false;
      
      if (!isCurrentLocaleEnabled) {
        // Current locale is not enabled, switch to default or first enabled language
        const defaultLanguage = currentTenant.languageDefault || 'en';
        const defaultEnabled = languagesEnabled[defaultLanguage] !== false;
        
        if (defaultEnabled) {
          setLocale(defaultLanguage as 'en' | 'ar-AE' | 'he');
        } else {
          // Find first enabled language
          const firstEnabled = Object.entries(languagesEnabled)
            .find(([_, enabled]) => enabled !== false)?.[0];
          
          if (firstEnabled) {
            setLocale(firstEnabled as 'en' | 'ar-AE' | 'he');
          }
        }
      }
    }
  }, [currentTenant, locale, setLocale]);

  // Track mode selection
  useEffect(() => {
    if (trackingInitialized) {
      trackingClient.track({ event_name: 'view_mode_picker', metadata: { mode } });
    }
  }, [mode, trackingInitialized]);

  // Cache utility functions
  const saveUserCache = (data: { fullName: string; phone: string; email: string }) => {
    try {
      const cacheData: UserProfileCache = {
        full_name: data.fullName,
        phone: data.phone,
        email: data.email,
        cached_at: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      // Silent fail - cache is not critical
    }
  };

  const loadUserCache = (): UserProfileCache | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const cacheData: UserProfileCache = JSON.parse(cached);
      const ageInDays = (Date.now() - cacheData.cached_at) / (1000 * 60 * 60 * 24);

      if (ageInDays > CACHE_EXPIRY_DAYS) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }

      return cacheData;
    } catch (error) {
      // Silent fail - cache is not critical
      return null;
    }
  };

  const clearUserCache = () => {
    try {
      localStorage.removeItem(CACHE_KEY);
      setIsCachedUser(false);
      toast({
        title: t('validation.cache_cleared'),
        description: t('validation.cache_cleared'),
      });
    } catch (error) {
      // Silent fail - cache is not critical
    }
  };

  // Load cached data on mount
  useEffect(() => {
    const cached = loadUserCache();
    if (cached) {
      setFormData({
        fullName: cached.full_name,
        phone: cached.phone,
        email: cached.email,
      });
      setIsCachedUser(true);
      
      const firstName = cached.full_name.split(" ")[0];
      toast({
        title: t('validation.welcome_back', { name: firstName }),
        description: t('validation.pre_filled'),
      });
    }
  }, []);

  const validateHairQuestionnaire = (): { valid: boolean; errors: string[]; fieldErrors: any } => {
    const errors: string[] = [];
    const fieldErrors: any = {};

    // Age validation
    const age = parseInt(hairQuestionnaire.age);
    if (!hairQuestionnaire.age || isNaN(age) || age < 13 || age > 99) {
      const errorMsg = t('validation.age_invalid');
      errors.push(errorMsg);
      fieldErrors.age = errorMsg;
    }

    // Gender validation
    if (!hairQuestionnaire.gender) {
      const errorMsg = t('validation.gender_required');
      errors.push(errorMsg);
      fieldErrors.gender = errorMsg;
    }

    // Medical history validation
    if (hairQuestionnaire.hasMedicalHistory) {
      const details = hairQuestionnaire.medicalHistoryDetails.trim();
      if (details.length < 10 || details.length > 800) {
        const errorMsg = t('validation.medical_history_brief');
        errors.push(errorMsg);
        fieldErrors.medicalHistoryDetails = errorMsg;
      }
    }

    // Family history validation
    if (hairQuestionnaire.hasFamilyHistory) {
      const details = hairQuestionnaire.familyHistoryDetails.trim();
      if (details.length < 10 || details.length > 800) {
        const errorMsg = t('validation.medical_history_brief');
        errors.push(errorMsg);
        fieldErrors.familyHistoryDetails = errorMsg;
      }
    }

    // Diet validation
    if (!hairQuestionnaire.diet) {
      const errorMsg = t('validation.diet_required');
      errors.push(errorMsg);
      fieldErrors.diet = errorMsg;
    }

    // Sleep hours validation
    const sleep = parseFloat(hairQuestionnaire.sleepHours);
    if (!hairQuestionnaire.sleepHours || isNaN(sleep) || sleep < 0 || sleep > 14) {
      const errorMsg = t('validation.sleep_invalid');
      errors.push(errorMsg);
      fieldErrors.sleepHours = errorMsg;
    }

    // Stress level validation
    if (!hairQuestionnaire.stressLevel) {
      const errorMsg = t('validation.stress_required');
      errors.push(errorMsg);
      fieldErrors.stressLevel = errorMsg;
    }

    // Hair care routine validation
    if (hairQuestionnaire.hairCareRoutine.length === 0) {
      const errorMsg = t('validation.hair_care_required');
      errors.push(errorMsg);
      fieldErrors.hairCareRoutine = errorMsg;
    }

    return { valid: errors.length === 0, errors, fieldErrors };
  };

  const validateForm = (): boolean => {
    // Sanitize inputs
    const sanitizedName = DOMPurify.sanitize(formData.fullName.trim());
    const sanitizedPhone = DOMPurify.sanitize(formData.phone.trim());
    const sanitizedEmail = DOMPurify.sanitize(formData.email.trim());

    // Full name validation
    const nameWords = sanitizedName.split(/\s+/).filter(word => word.length > 0);
    if (nameWords.length < 2) {
      toast({
        title: t('validation.name_required'),
        description: t('validation.name_required'),
        variant: "destructive",
      });
      return false;
    }
    
    if (!/^[a-zA-Z\s\-]+$/.test(sanitizedName)) {
      toast({
        title: t('validation.name_invalid'),
        description: t('validation.name_invalid'),
        variant: "destructive",
      });
      return false;
    }

    // Phone validation
    const phoneDigits = sanitizedPhone.replace(/[^\d]/g, '');
    if (phoneDigits.length < 9 || phoneDigits.length > 15) {
      toast({
        title: t('validation.phone_invalid'),
        description: t('validation.phone_invalid'),
        variant: "destructive",
      });
      return false;
    }

    if (/^(\d)\1+$/.test(phoneDigits)) {
      toast({
        title: t('validation.phone_invalid'),
        description: t('validation.phone_invalid'),
        variant: "destructive",
      });
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitizedEmail)) {
      toast({
        title: t('validation.email_invalid'),
        description: t('validation.email_invalid'),
        variant: "destructive",
      });
      return false;
    }

    // Photo validation
    if (mode === "skin") {
      if (!skinPhotos.front || !skinPhotos.left || !skinPhotos.right) {
        toast({
          title: t('validation.photo_missing'),
          description: t('validation.photos_missing', { photos: 'Front, Left, Right' }),
          variant: "destructive",
        });
        return false;
      }
    } else {
      if (!hairPhoto) {
        toast({
          title: t('validation.photo_missing'),
          description: t('validation.photo_missing'),
          variant: "destructive",
        });
        return false;
      }
    }

    // Consent validation
    if (!emailConsent) {
      toast({
        title: t('validation.consent_required'),
        description: t('validation.consent_required'),
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const compressImage = (base64: string, maxWidth: number = 600): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxWidth) {
          if (width > height) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          } else {
            width = (width * maxWidth) / height;
            height = maxWidth;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        const compressed = canvas.toDataURL('image/jpeg', 0.6);
        resolve(compressed);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = base64;
    });
  };

  const handleSkinPhotoSelect = (position: 'front' | 'left' | 'right', file: File | null, base64: string | null) => {
    if (base64) {
      setSkinPhotos(prev => ({ ...prev, [position]: base64 }));
    } else {
      setSkinPhotos(prev => ({ ...prev, [position]: "" }));
    }
  };

  const handleHairPhotoSelect = async (file: File | null, base64: string | null) => {
    if (!file || !base64) {
      setHairPhoto(null);
      return;
    }

    try {
      const compressed = await compressImage(base64);
      setHairPhoto(compressed);
    } catch (error) {
      console.error('Image compression error:', error);
      toast({
        title: t('errors.image_error'),
        description: t('errors.image_process_failed'),
        variant: "destructive",
      });
    }
  };

  const generatePDFBase64 = async (analysisText: string, photos: { front: string; left: string; right: string } | string | null): Promise<string> => {
    // Dynamically import html2pdf only when needed (lazy loading for performance)
    const html2pdf = (await import('html2pdf.js')).default;
    
    const isRTL = locale === 'ar-AE';
    const tenantName = currentTenant?.name || 'Savoy Clinic';
    const tenantLogo = currentTenant?.logoUrl || logoSavoy;
    const tenantPrimaryColor = currentTenant?.primaryColor || '#03178A';
    
    // Create HTML content for PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <meta charset="UTF-8">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
          
          body {
            font-family: ${isRTL ? "'Cairo', 'IBM Plex Sans Arabic'" : 'Arial'}, sans-serif;
            direction: ${isRTL ? 'rtl' : 'ltr'};
            margin: 0;
            padding: 20px;
            font-size: 11px;
            line-height: 1.6;
          }
          
          .header {
            background-color: ${tenantPrimaryColor};
            color: white;
            padding: 30px;
            text-align: center;
            margin: -20px -20px 20px -20px;
          }
          
          .header img {
            max-width: 150px;
            height: auto;
            margin-bottom: 10px;
            display: block;
            margin-left: auto;
            margin-right: auto;
          }
          
          .header .subtitle {
            font-size: 12px;
            opacity: 0.9;
          }
          
          .title {
            color: #03178A;
            font-size: 20px;
            font-weight: bold;
            text-align: center;
            margin: 20px 0;
          }
          
          .client-info {
            background-color: #F5F7FA;
            padding: 15px;
            border-radius: 8px;
            margin: 20px 0;
          }
          
          .photos {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin: 20px 0;
            flex-wrap: wrap;
          }
          
          .photo-container {
            text-align: center;
          }
          
          .photo-container img {
            width: 150px;
            height: 150px;
            object-fit: cover;
            border-radius: 4px;
          }
          
          .photo-label {
            font-size: 9px;
            color: #666;
            margin-top: 5px;
          }
          
          .section-header {
            background-color: ${tenantPrimaryColor};
            color: white;
            padding: 8px 12px;
            font-weight: bold;
            font-size: 13px;
            margin: 20px 0 10px 0;
            border-radius: 4px;
          }
          
          .treatment-name {
            color: ${tenantPrimaryColor};
            font-weight: bold;
            font-size: 12px;
            margin: 15px 0 8px 0;
          }
          
          .subsection {
            font-weight: bold;
            font-size: 11px;
            margin: 10px 0 5px 0;
          }
          
          .bullet, .paragraph {
            margin: 5px 0;
            padding-${isRTL ? 'right' : 'left'}: ${isRTL ? '0' : '20px'};
            padding-${isRTL ? 'left' : 'right'}: ${isRTL ? '20px' : '0'};
          }
          
          .footer {
            text-align: center;
            color: #999;
            font-size: 9px;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #eee;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${tenantLogo}" alt="${tenantName}" />
          <div class="subtitle">${mode === "skin" ? t('pdf.subtitle_skin') : t('pdf.subtitle_hair')}</div>
        </div>
        
        <div class="title">${mode === "skin" ? t('pdf.title_skin') : t('pdf.title_hair')}</div>
        
        <div class="client-info">
          <div><strong>${t('pdf.client')}</strong> ${formData.fullName || 'N/A'}</div>
          <div><strong>${t('pdf.date')}</strong> ${
            locale === 'ar-AE' 
              ? new Intl.DateTimeFormat('ar-AE', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date())
              : new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date())
          }</div>
        </div>
        
        ${photos ? `
          <h3 class="section-header">${mode === "skin" ? t('pdf.reference_photos') : t('pdf.reference_photo')}</h3>
          <div class="photos">
            ${typeof photos === 'object' && 'front' in photos ? `
              <div class="photo-container">
                <img src="${photos.front}" alt="${t('pdf.front_view')}" />
                <div class="photo-label">${t('pdf.front_view')}</div>
              </div>
              <div class="photo-container">
                <img src="${photos.left}" alt="${t('pdf.left_side')}" />
                <div class="photo-label">${t('pdf.left_side')}</div>
              </div>
              <div class="photo-container">
                <img src="${photos.right}" alt="${t('pdf.right_side')}" />
                <div class="photo-label">${t('pdf.right_side')}</div>
              </div>
            ` : `
              <div class="photo-container">
                <img src="${photos}" alt="${mode === 'skin' ? t('pdf.front_view') : t('pdf.top_view')}" />
                <div class="photo-label">${mode === 'skin' ? t('pdf.front_view') : t('pdf.top_view')}</div>
              </div>
            `}
          </div>
        ` : ''}
        
        <div class="analysis-content">
          ${analysisText.split(/SECTION \d+/i).filter(s => s.trim()).map((section, sectionIndex) => {
            const lines = section.trim().split("\n").filter(line => line.trim());
            
            // For skin mode Section 3 (index 2), inject the exact outcome text
            if (mode === "skin" && sectionIndex === 2) {
              return `
                <div class="section-header">${locale === 'ar-AE' ? 'النتيجة المستهدفة' : 'Target Result'}</div>
                <div class="paragraph">${locale === 'ar-AE' 
                  ? 'هدفنا هو مساعدتك على تحقيق بشرة مثالية - ناعمة ومشرقة، خالية من التجاعيد والبقع الداكنة، مع ترطيب متوازن وتعزيز إنتاج الكولاجين.'
                  : 'Our goal is to help you achieve perfect skin – smooth and radiant, free from wrinkles and dark spots, with balanced hydration and enhanced collagen production.'
                }</div>
              `;
            }
            
            return lines.map(line => {
              const trimmed = line.trim();
              
              if (trimmed.match(/^[–-]\s*.+$/)) {
                return `<div class="section-header">${trimmed.replace(/^[–-]\s*/, "")}</div>`;
              } else if (trimmed.match(/^\*\*.+\*\*$/)) {
                return `<div class="treatment-name">${trimmed.replace(/\*\*/g, "")}</div>`;
              } else if (trimmed.match(/^.+:$/)) {
                return `<div class="subsection">${trimmed}</div>`;
              } else if (trimmed.startsWith("•") || trimmed.startsWith("-")) {
                return `<div class="bullet">${trimmed}</div>`;
              } else if (trimmed) {
                return `<div class="paragraph">${trimmed}</div>`;
              }
              return '';
            }).join('');
          }).join('')}
        </div>
        
        <div class="footer">
          ${tenantName} | Professional Medical Aesthetics
        </div>
      </body>
      </html>
    `;
    
    const opt = {
      margin: [10, 10, 10, 10] as [number, number, number, number],
      filename: 'report.pdf',
      image: { type: 'jpeg' as const, quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const }
    };
    
    const pdf = await html2pdf().from(htmlContent).set(opt).outputPdf('datauristring');
    return pdf.split(',')[1];
  };

  const handleAnalyze = async () => {
    if (!validateForm()) {
      return;
    }

    // Track form validation success
    trackingClient.track({
      event_name: 'form_validated',
      metadata: { mode }
    }, {
      email: formData.email,
      phone: formData.phone,
      country: locale === 'ar-AE' ? 'AE' : undefined
    });

    // Check quota before starting analysis
    if (currentTenant) {
      const quotaPercentage = (currentTenant.quotaUsed / currentTenant.monthlyQuota) * 100;
      
      if (quotaPercentage >= 100) {
        toast({
          variant: 'destructive',
          title: t('errors.quota_exceeded') || 'Quota Exceeded',
          description: t('errors.quota_exceeded_message') || 'Your monthly report quota has been reached. Please upgrade your plan or wait for the next billing cycle.',
        });
        return;
      }
      
      // Show warning if quota is getting low
      if (quotaPercentage >= 90 && quotaPercentage < 100) {
        toast({
          variant: 'destructive',
          title: 'Quota Warning',
          description: `You have used ${currentTenant.quotaUsed} of ${currentTenant.monthlyQuota} reports (${quotaPercentage.toFixed(0)}%). Only ${currentTenant.monthlyQuota - currentTenant.quotaUsed} reports remaining.`,
        });
      } else if (quotaPercentage >= 80 && quotaPercentage < 90) {
        toast({
          title: 'Quota Notice',
          description: `You have used ${quotaPercentage.toFixed(0)}% of your monthly quota.`,
        });
      }
    }

    const userEmail = DOMPurify.sanitize(formData.email.trim());
    const userFullName = DOMPurify.sanitize(formData.fullName.trim());
    const userPhone = DOMPurify.sanitize(formData.phone.trim());

    setIsAnalyzing(true);
    setCurrentStep(0);

    const stepTimings = [10000, 45000, 25000, 10000];

    for (let i = 0; i < STEPS.length; i++) {
      setTimeout(() => {
        setCurrentStep(i + 1);
      }, stepTimings.slice(0, i + 1).reduce((a, b) => a + b, 0));
    }

    try {
      await new Promise(resolve => setTimeout(resolve, 10000));

      const { data: analysisData, error: analysisError } = await supabase.functions.invoke(
        "analyze-face",
        {
          body: {
            mode,
            photos: mode === "skin" ? skinPhotos : undefined,
            photo: mode === "hair" ? hairPhoto : undefined,
            questionnaire: mode === "hair" ? hairQuestionnaire : undefined,
            locale,
            email: userEmail,
            fullName: userFullName,
            phone: userPhone,
            tenantId: currentTenant?.id,
          },
        }
      );

      if (analysisError || !analysisData || analysisData.success === false || !analysisData?.analysis) {
        console.error("Analysis error:", analysisError || analysisData);
        
        let errorMessage = t('errors.analysis_failed');
        let errorTitle = t('errors.analysis_error');
        
        const actualError = analysisData?.error || analysisError?.message || "Unknown error occurred";
        
        // Check for insufficient balance specifically
        if (analysisData?.insufficient_balance || actualError.includes("Insufficient wallet balance")) {
          errorTitle = "Insufficient Balance";
          errorMessage = `${actualError} Please go to the Dashboard to top up your wallet.`;
          toast({
            title: errorTitle,
            description: errorMessage,
            variant: "destructive",
          });
          setIsAnalyzing(false);
          return;
        }
        
        if (typeof actualError === "string") {
          if (actualError.includes("dark") || actualError.includes("lighting")) {
            errorTitle = t('errors.poor_lighting');
            errorMessage = actualError;
          } else if (actualError.includes("blurry") || actualError.includes("focus")) {
            errorTitle = t('errors.image_quality');
            errorMessage = actualError;
          } else if (actualError.includes("face") || actualError.includes("scalp") || actualError.includes("obstructed")) {
            errorTitle = t('errors.photo_detection');
            errorMessage = actualError;
          } else if (actualError.includes("Rate limit") || actualError.includes("429")) {
            errorTitle = t('errors.rate_limit');
            errorMessage = t('errors.rate_limit_message');
          } else if (actualError.includes("Payment") || actualError.includes("unavailable") || actualError.includes("402")) {
            errorTitle = t('errors.service_unavailable');
            errorMessage = t('errors.service_unavailable_message');
          } else {
            errorMessage = actualError;
          }
        }
        
        toast({
          title: errorTitle,
          description: errorMessage,
          variant: "destructive",
        });
        setIsAnalyzing(false);
        return;
      }

      let pdfBase64: string;
      try {
        pdfBase64 = await generatePDFBase64(
          analysisData.analysis,
          mode === "skin" ? skinPhotos : hairPhoto
        );
        
        if (!pdfBase64 || pdfBase64.length === 0) {
          throw new Error('PDF generation returned empty data');
        }
      } catch (pdfError: any) {
        toast({
          title: t('errors.pdf_generation'),
          description: t('errors.pdf_generation_message'),
          variant: "destructive",
        });
        setIsAnalyzing(false);
        return;
      }

      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const fileName = `reports/${Date.now()}_${userFullName.replace(/\s+/g, '_')}.pdf`;
      
      const { error: uploadError } = await supabase.storage
        .from('reports')
        .upload(fileName, blob, {
          contentType: 'application/pdf',
          upsert: false
        });

      if (uploadError) {
        toast({
          title: t('errors.upload_error'),
          description: t('errors.upload_error_message'),
          variant: "destructive",
        });
        setIsAnalyzing(false);
        return;
      }

      const { data: urlData } = supabase.storage
        .from('reports')
        .getPublicUrl(fileName);

      const emailPayload = {
        email: userEmail,
        fullName: userFullName,
        analysis: analysisData.analysis,
        mode,
        locale,
        tenantId: currentTenant?.id,
      };

      const { data: emailData, error: emailError } = await supabase.functions.invoke(
        "send-report-email",
        {
          body: emailPayload,
        }
      );

      if (emailError || !emailData || !emailData.success) {
        trackingClient.track({
          event_name: 'error_send_failed',
          metadata: { channel: 'client', mode }
        });
        toast({
          title: t('errors.email_failed'),
          description: t('errors.email_failed_message'),
          variant: "destructive",
        });
        setIsAnalyzing(false);
        return;
      }

      // Show success notification
      toast({
        title: t('analysis.success.report_sent'),
        description: t('analysis.success.report_sent_message'),
      });

      // Track successful email
      trackingClient.track({
        event_name: 'email_sent_client',
        metadata: { mode }
      });

      const { error: leadError } = await supabase.functions.invoke(
        "send-lead-to-frontdesk",
        {
          body: {
            fullName: userFullName,
            phone: userPhone,
            email: userEmail,
            tenantId: currentTenant?.id,
            mode,
          },
        }
      );

      if (leadError) {
        console.error("Lead forward error:", leadError);
        trackingClient.track({
          event_name: 'error_send_failed',
          metadata: { channel: 'frontdesk', mode }
        });
      } else {
        trackingClient.track({
          event_name: 'lead_forwarded_frontdesk',
          metadata: { mode }
        });
      }

      // Track usage and increment quota
      if (currentTenant) {
        const { error: usageError } = await supabase.functions.invoke(
          "track-usage",
          {
            body: {
              tenantId: currentTenant.id,
              eventType: 'report_generated',
              eventSubtype: mode,
              metadata: {
                email: userEmail,
                mode,
                locale,
              },
            },
          }
        );

        if (usageError) {
          console.error("Usage tracking error:", usageError);
        }

        // Refresh tenant to get updated quota
        await refreshTenant();
      }

      saveUserCache({
        fullName: userFullName,
        phone: userPhone,
        email: userEmail,
      });

      // Track report generation and confirmation
      trackingClient.track({
        event_name: 'report_generated',
        metadata: { mode }
      });

      trackingClient.track({
        event_name: 'report_confirm_shown',
        metadata: { mode }
      });

      setAnalysisComplete(true);
      setIsAnalyzing(false);

    } catch (error: any) {
      toast({
        title: t('errors.processing_failed'),
        description: error.message || t('errors.processing_failed_message'),
        variant: "destructive",
      });
      setIsAnalyzing(false);
    }
  };

  const handleReset = () => {
    setFormData({ fullName: "", phone: "", email: "" });
    setHairQuestionnaire({
      age: "",
      gender: "",
      hasMedicalHistory: false,
      medicalHistoryDetails: "",
      hasFamilyHistory: false,
      familyHistoryDetails: "",
      diet: "",
      sleepHours: "",
      stressLevel: "",
      hairCareRoutine: [],
      hairCareNotes: "",
    });
    setQuestionnaireComplete(false);
    setQuestionnaireErrors({});
    setEmailConsent(false);
    setSkinPhotos({ front: "", left: "", right: "" });
    setHairPhoto(null);
    setAnalysisComplete(false);
    setCurrentStep(0);
    setMode("skin");
  };

  const handleQuestionnaireChange = (data: HairQuestionnaireData) => {
    setHairQuestionnaire(data);
    // Clear errors when user makes changes
    if (Object.keys(questionnaireErrors).length > 0) {
      setQuestionnaireErrors({});
    }
  };

  const handleQuestionnaireSubmit = () => {
    const validation = validateHairQuestionnaire();
    
    if (!validation.valid) {
      setQuestionnaireErrors(validation.fieldErrors);
      toast({
        title: t('errors.incomplete_questionnaire'),
        description: validation.errors[0],
        variant: "destructive",
      });
      return;
    }

    setQuestionnaireErrors({});
    setQuestionnaireComplete(true);
    toast({
      title: t('questionnaire.title'),
      description: t('questionnaire.subtitle'),
    });
  };

  if (analysisComplete) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <div className="max-w-2xl w-full text-center space-y-8 animate-fade-in">
          <div className="flex justify-center">
            <div className="relative">
              <div 
                className="absolute inset-0 blur-3xl rounded-full"
                style={{
                  backgroundColor: currentTenant?.primaryColor ? `${currentTenant.primaryColor}33` : undefined,
                }}
              ></div>
              <CheckCircle2 
                className="w-24 h-24 relative" 
                strokeWidth={1.5}
                style={{ color: currentTenant?.primaryColor || undefined }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h1 className="text-3xl md:text-4xl font-light text-foreground">
              {t('confirm.success')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
              {t('confirm.check_email')}
            </p>
            <p className="text-sm text-muted-foreground">
              {formData.email}
            </p>
          </div>

          <div className="bg-card rounded-2xl p-8 shadow-medium border border-border space-y-4">
            <p className="text-foreground font-medium">{t('pdf.next_steps')}</p>
            <p className="text-muted-foreground leading-relaxed">
              {t('confirm.spam_notice')}
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground pt-2">
              <Mail className="w-4 h-4" />
              <span>{currentTenant?.frontdeskEmail || 'frontdesk@savoyclinic.ae'}</span>
            </div>
          </div>

          <Button
            onClick={handleReset}
            variant="outline"
            size="lg"
            className="min-h-[44px] px-8"
          >
            {t('confirm.new_analysis')}
          </Button>
        </div>
      </div>
    );
  }

  if (isAnalyzing) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <ProgressTimeline steps={STEPS} currentStep={currentStep} />
      </div>
    );
  }

  // Show loading state while tenant data is being fetched
  if (tenantLoading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <header 
        className="border-b border-primary-light/20 sticky top-0 z-50 shadow-medium"
        style={{
          background: currentTenant?.primaryColor 
            ? `linear-gradient(135deg, ${currentTenant.primaryColor}, ${currentTenant.primaryColor}dd)`
            : undefined,
          ...(currentTenant?.primaryColor ? {} : { background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary)) / 0.9)' })
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1"></div>
            <img
              src={currentTenant?.logoUrl || logoSavoy}
              alt={currentTenant?.name || "Savoy Clinic"}
              width="121"
              height="56"
              loading="eager"
              fetchPriority="high"
              className="h-14 md:h-18 drop-shadow-lg"
            />
            <div className="flex-1 flex justify-end items-center gap-3">
              <FlagLanguageSwitcher />
            </div>
          </div>
          <p className="text-sm md:text-base text-white/90 font-light tracking-wide text-center">
            {t('hero.subtitle')}
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12 space-y-12 pb-64 md:pb-56">
        {/* Mode Selection */}
        <section className="text-center space-y-6 max-w-3xl mx-auto">
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border"
            style={{
              backgroundColor: currentTenant?.primaryColor ? `${currentTenant.primaryColor}1A` : undefined,
              color: currentTenant?.primaryColor || undefined,
              borderColor: currentTenant?.primaryColor ? `${currentTenant.primaryColor}33` : undefined,
            }}
          >
            <Sparkles className="w-4 h-4" />
            <span>{t('hero.badge')}</span>
          </div>
          
          <h1 className="text-3xl md:text-5xl font-light text-foreground leading-tight">
            {t('hero.title')}
          </h1>
          
          {/* Show mode selector only if both flows are enabled */}
          {currentTenant?.skinFlowEnabled && currentTenant?.hairFlowEnabled && (
            <div className="flex gap-4 justify-center flex-wrap">
              <Button
                onClick={() => setMode("skin")}
                variant={mode === "skin" ? "default" : "outline"}
                size="lg"
                className="min-h-[56px] px-8 rounded-full"
                style={mode === "skin" && currentTenant?.primaryColor ? {
                  backgroundColor: currentTenant.primaryColor,
                  borderColor: currentTenant.primaryColor,
                  color: 'white'
                } : {}}
              >
                {t('hero.mode_skin')}
              </Button>
              <Button
                onClick={() => setMode("hair")}
                variant={mode === "hair" ? "default" : "outline"}
                size="lg"
                className="min-h-[56px] px-8 rounded-full"
                style={mode === "hair" && currentTenant?.primaryColor ? {
                  backgroundColor: currentTenant.primaryColor,
                  borderColor: currentTenant.primaryColor,
                  color: 'white'
                } : {}}
              >
                {t('hero.mode_hair')}
              </Button>
            </div>
          )}
          
          {/* Show error if no flows are enabled */}
          {currentTenant && !currentTenant.skinFlowEnabled && !currentTenant.hairFlowEnabled && (
            <div className="p-6 bg-destructive/10 border border-destructive/20 rounded-xl text-center">
              <p className="text-destructive font-medium">
                No analysis flows are currently available. Please contact the clinic.
              </p>
            </div>
          )}

          {mode && (
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              {mode === "skin" 
                ? t('photo.instructions_skin')
                : t('photo.instructions_hair')}
            </p>
          )}
        </section>

        {/* Client Form - only show if mode is selected and flow is enabled */}
        {mode && ((mode === "skin" && currentTenant?.skinFlowEnabled) || (mode === "hair" && currentTenant?.hairFlowEnabled)) && (
        <section className="max-w-2xl mx-auto">
          <div className="bg-card rounded-3xl p-6 md:p-10 shadow-medium border border-border space-y-6">
            <div className="space-y-2 text-center">
              <h2 className="text-2xl font-light text-foreground">{t('form.title')}</h2>
              <p className="text-sm text-muted-foreground">{t('form.subtitle')}</p>
            </div>

            {isCachedUser && (
              <div 
                className="flex items-center justify-between p-4 rounded-xl border"
                style={{
                  backgroundColor: currentTenant?.primaryColor ? `${currentTenant.primaryColor}1A` : undefined,
                  borderColor: currentTenant?.primaryColor ? `${currentTenant.primaryColor}33` : undefined,
                }}
              >
                <div className="flex items-center gap-3">
                  <CheckCircle2 
                    className="w-5 h-5 flex-shrink-0"
                    style={{ color: currentTenant?.primaryColor || undefined }}
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground">{t('form.cache_notice')}</p>
                    <p className="text-xs text-muted-foreground">{t('form.cache_notice')}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearUserCache}
                  className="text-xs h-8 px-3 hover:bg-destructive/10 hover:text-destructive"
                >
                  <X className="w-3 h-3 mr-1" />
                  {t('form.clear_cache')}
                </Button>
              </div>
            )}

            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-sm font-medium flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  {t('form.full_name.label')}
                </Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder={t('form.full_name.placeholder')}
                  value={formData.fullName}
                  onChange={(e) => {
                    setFormData({ ...formData, fullName: e.target.value });
                    if (!formStartTracked && trackingInitialized) {
                      trackingClient.track({ event_name: 'form_started', metadata: { mode } });
                      setFormStartTracked(true);
                    }
                  }}
                  className="h-12 rounded-xl"
                  disabled={isAnalyzing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  {t('form.phone.label')}
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder={t('form.phone.placeholder')}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-12 rounded-xl"
                  disabled={isAnalyzing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  {t('form.email.label')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('form.email.placeholder')}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-12 rounded-xl"
                  disabled={isAnalyzing}
                />
              </div>

              <div className="flex items-start space-x-3 p-4 rounded-xl bg-muted/50 border border-border">
                <Checkbox 
                  id="email-consent"
                  checked={emailConsent}
                  onCheckedChange={(checked) => setEmailConsent(checked as boolean)}
                  disabled={isAnalyzing}
                  className="mt-1"
                />
                <label 
                  htmlFor="email-consent" 
                  className="text-sm leading-relaxed cursor-pointer select-none text-foreground"
                >
                  {t('form.consent')}
                </label>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* Hair Questionnaire (only for hair mode) */}
        {mode === "hair" && currentTenant?.hairFlowEnabled && !questionnaireComplete && (
          <section className="max-w-2xl mx-auto">
            <div className="bg-card rounded-3xl p-6 md:p-10 shadow-medium border border-border space-y-6">
              <div className="space-y-2 text-center">
                <h2 className="text-2xl font-light text-foreground">{t('questionnaire.intro_title')}</h2>
                <p className="text-sm text-muted-foreground">{t('questionnaire.intro_subtitle')}</p>
              </div>

              <HairQuestionnaire 
                data={hairQuestionnaire}
                onChange={handleQuestionnaireChange}
                disabled={isAnalyzing}
                errors={questionnaireErrors}
              />

              <div className="pt-4">
                <Button
                  onClick={handleQuestionnaireSubmit}
                  size="lg"
                  className="w-full h-14 rounded-xl text-base text-white"
                  disabled={isAnalyzing}
                  style={{
                    background: currentTenant?.primaryColor 
                      ? `linear-gradient(135deg, ${currentTenant.primaryColor}, ${currentTenant.primaryColor}dd)`
                      : undefined,
                  }}
                >
                  {t('questionnaire.continue_button')}
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* Photo Upload */}
        {mode && ((mode === "skin" && currentTenant?.skinFlowEnabled) || (mode === "hair" && currentTenant?.hairFlowEnabled && questionnaireComplete)) && (
          <section className="space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-light text-foreground">{mode === "skin" ? t('hero.mode_skin') : t('hero.mode_hair')}</h2>
              <p className="text-sm text-muted-foreground max-w-xl mx-auto">
                {mode === "skin" 
                  ? t('photo.instructions_skin')
                  : t('photo.instructions_hair')}
              </p>
            </div>

            <div className="max-w-md mx-auto">
              {mode === "skin" ? (
                <MultiFileUpload
                  onFileSelect={handleSkinPhotoSelect}
                  isAnalyzing={isAnalyzing}
                  images={skinPhotos}
                />
              ) : (
                <FileUpload
                  onFileSelect={handleHairPhotoSelect}
                  isAnalyzing={isAnalyzing}
                  currentImage={hairPhoto}
                  label={t('pdf.top_view')}
                />
              )}
            </div>
          </section>
        )}

        {/* CTA Button */}
        <div className="hidden md:flex justify-center pb-8">
          {((mode === "skin" && skinPhotos.front && skinPhotos.left && skinPhotos.right) || 
            (mode === "hair" && questionnaireComplete && hairPhoto)) && (
            <Button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              size="lg"
              className="min-h-[56px] px-12 rounded-full text-base font-medium text-white transition-opacity shadow-luxury hover:shadow-strong"
              style={{
                background: currentTenant?.primaryColor 
                  ? `linear-gradient(135deg, ${currentTenant.primaryColor}, ${currentTenant.primaryColor}dd)`
                  : undefined,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {t('cta.start')}
            </Button>
          )}
        </div>
      </main>

      {/* Sticky Mobile CTA */}
      {((mode === "skin" && skinPhotos.front && skinPhotos.left && skinPhotos.right) || 
        (mode === "hair" && questionnaireComplete && hairPhoto)) && (
        <div className="md:hidden fixed bottom-[104px] left-0 right-0 p-4 bg-gradient-to-t from-background via-background to-transparent pointer-events-none z-40">
          <div className="pointer-events-auto">
            <Button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              size="lg"
              className="w-full min-h-[56px] rounded-full text-base font-medium text-white transition-opacity shadow-luxury"
              style={{
                background: currentTenant?.primaryColor 
                  ? `linear-gradient(135deg, ${currentTenant.primaryColor}, ${currentTenant.primaryColor}dd)`
                  : undefined,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {t('cta.getReport')}
            </Button>
          </div>
        </div>
      )}

      {currentTenant?.trustSectionEnabled && <TrustSection />}
      <Footer />
      <ConsentBanner />

      {/* Report Counter Screen - shown after analysis completes */}
      {analysisComplete && (
        <ReportCounterScreen
          onComplete={() => {
            // Called when counter finishes
            console.log("Counter complete");
          }}
          onViewReport={() => {
            // Optional: implement view report functionality
            console.log("View report");
          }}
          email={formData.email}
          onHome={() => {
            navigate('/');
            setAnalysisComplete(false);
          }}
          onReset={handleReset}
          duration={30}
        />
      )}
    </div>
  );
}
